// 事件策略映射（提取为常量）
const EVENT_STRATEGIES = {
    'click': (self) => (event, scope, modelKey) => (e) => {
        e.stopPropagation();
        self.eventHandler(scope, event.function);
    },
    'input': (self) => (event, scope, modelKey) => (value) => {
        if (self.$listeners['update:params']) {
            const newFormData = {...self.formData, [modelKey]: value};
            self.$emit('update:params', newFormData);
        }
    },
    'change': (self) => (event, scope, modelKey) => (value) => {
        if (self.$listeners['update:params']) {
            self.formData[modelKey] = value;
            self.$emit('update:params', self.formData);
        }
    },
};

const NATIVE_EVENT_STRATEGIES = {
    'hover': (self) => (event, scope, modelKey) => {
        return {
            mouseenter: (e) => self.eventHandler(scope, event.function, {eventType: 'mouseenter'}),
            mouseleave: (e) => self.eventHandler(scope, event.function, {eventType: 'mouseleave'})
        };
    },
    'focus': (self) => (event, scope, modelKey) => (e) => {
        self.eventHandler(scope, event.function);
    },
    'blur': (self) => (event, scope, modelKey) => (e) => {
        self.eventHandler(scope, event.function);
    },
    'keyup': (self) => (event, scope, modelKey) => (value) => {
        if (value && (value.key === 'Enter' || value.keyCode === 13)) {
            self.eventHandler(scope, event.function);
        }
    },
};
export default {
    props: {},
    methods: {
        // 公共事件绑定逻辑
        bindBaseEvents(events, scope, modelKey, strategies) {
            const self = this;
            const handlers = {};

            // 参数校验
            if (!Array.isArray(events)) {
                return handlers;
            }

            // 缓存作用域数据
            const row = scope && scope.row ? scope.row : null;

            for (const event of events) {
                // 类型安全校验：明确 event.type 和 event.function 类型
                if (
                    event === null ||
                    typeof event !== 'object' ||
                    typeof event.type !== 'string' ||
                    !event.function ||
                    !['string', 'function'].includes(typeof event.function)
                ) {
                    continue;
                }
                // 事件类型处理策略
                const strategy = strategies[event.type];
                if (!strategy) {
                    console.warn(`不支持的事件类型: ${event.type}`);
                    continue;
                }
                // 执行策略生成处理器
                const handler = strategy(self)(event, row, modelKey);

                // 处理特殊返回结构（如hover返回多个事件）
                if (event.type === 'hover' && typeof handler === 'object') {
                    Object.entries(handler).forEach(([subType, subHandler]) => {
                        handlers[subType] = subHandler;
                    });
                } else {
                    handlers[event.type] = handler;
                }
            }

            return handlers;
        },

        bindEvents(config, scope, modelKey = null) {
            if (!config) return {};
            const events = config['events'];
            if (modelKey == null) {
                modelKey = config.modelKey;
            }
            if (!events) return {};
            return this.bindBaseEvents(events, scope, modelKey, EVENT_STRATEGIES);
        },

        bindNativeEvents(config, scope) {
            if (!config) return {};
            const events = config['nativeEvents'];
            const modelKey = config.modelKey;
            return this.bindBaseEvents(events, scope, modelKey, NATIVE_EVENT_STRATEGIES);
        },

        eventHandler(payload, handler, context = this.parentCtx || this) {
            // 使用示例
            // this.eventHandler(rowData, 'saveData' ); // 通过名称调用
            // this.eventHandler(rowData, (data) => {...}); // 直接传递函数
            // this.eventHandler(rowData, 'saveData', customContext); // 自定义上下文
            if (!handler) return;

            let fn;
            if (typeof handler === 'string') {
                if (!context) {
                    console.warn('事件处理失败：上下文缺失，无法通过字符串名称调用 handler', {handler});
                    return;
                }
                if (typeof context[handler] !== 'function') {
                    console.warn(`事件处理失败：handler "${handler}" 未在上下文中定义或非函数`, {context});
                    return;
                }
                fn = context[handler];
            } else if (typeof handler === 'function') {
                fn = handler;
            } else {
                console.warn('事件处理失败：handler 必须是字符串或函数', {handler});
                return;
            }
            try {
                return payload ? fn.call(context, payload) : fn.call(context);
            } catch (error) {
                console.error('事件触发失败:', {
                    handler,
                    error,
                    payloadType: payload === null ? 'null' : typeof payload,
                    payloadConstructor: payload && payload.constructor && payload.constructor.name
                });
                this.$emit('event emit error', error);
            }
        },

        // 数据访问方法
        getNestedValue(obj, path) {
            if (!path) return obj;

            const pathArr = typeof path === 'string' ? path.split('.') : path;
            return pathArr.reduce((acc, key) => {
                if (!acc || !Object.prototype.hasOwnProperty.call(acc, key)) {
                    return undefined;
                }
                return acc[key];
            }, obj);
        },

        // 空值处理
        sanitizeValue(value) {
            return (
                value == null ||
                value === '' ||
                (Array.isArray(value) && value.length === 0) ||
                (typeof value === 'object' && value !== null && Object.keys(value).length === 0) ||
                (typeof value === 'number' && isNaN(value))
            ) ? '-' : value;
        },

        // 状态处理
        handleStatusMapping(id, statusMap) {
            let tag = 'info';
            let name = '未知';

            if (statusMap) {
                // 处理 tagType 映射
                if (statusMap.tagType && Object.prototype.hasOwnProperty.call(statusMap.tagType, id)) {
                    tag = statusMap.tagType[id];
                }

                // 处理 resultStatusOption 映射
                if (statusMap.resultStatusOption && Object.prototype.hasOwnProperty.call(statusMap.resultStatusOption, id)) {
                    name = statusMap.resultStatusOption[id];
                }
            } else if (this.$common && this.$common.CommonStatusList) {
                const statusList = this.$common.CommonStatusList;
                const found = statusList.find(item => item.id === id);
                if (found) {
                    tag = found.tag;
                    name = found.name;
                }
            } else {
                console.warn('未找到状态映射或全局状态列表');
            }

            return {tag, name};
        },

        // 图片URL生成
        buildImageUrl(obj, config, type) {
            // 获取基础URL
            const src = this.getNestedValue(obj, config.src);

            // 空值保护
            if (src === "-") return src;

            // 构建不同类型的URL
            if (type === 'src') {
                // 处理后缀配置
                const suffix = config.suffix || '';
                if (this.$common && this.$common[suffix]) {
                    return `${src}${this.$common[suffix]}`;
                }
                return src;
            }

            return src;
        },

        // 标签处理
        getLabel(field) {
            // 检查field和config是否存在
            if (!field || !field.config) {
                return '';
            }
            // 获取label键
            const labelKey = field.config.label;
            if (!labelKey) {
                return '';
            }
            // 获取翻译值
            const translated = this.$t(labelKey);
            // 如果translated等于labelKey，说明没有翻译，使用默认labelKey
            // 这里假设labelKey本身就是默认文本（如中文）
            // 如果用户希望当没有翻译时显示空，则需要其他处理，但根据问题描述，应使用默认label
            return translated !== labelKey ? translated : labelKey;
        },

        getFieldLabel(field) {
            const label = this.getLabel(field);
            return label ? `${label}:` : label;
        }
    },
    errorCaptured(err, vm, info) {
        console.error('组件异常捕获:', {
            error: err,
            component: vm.$options.name,
            info: info
        });
        this.$emit('critical-error', err);
        // 返回 false 阻止错误继续传播
        return false;
    }
};
